import React, { useRef, useState, useEffect } from "react";
import {
  Lightbulb, Zap, Users, Shield, Star, Heart, Award, TrendingUp,
  CheckCircle, Globe, Handshake, Target, Gem, Key, Leaf, Smile,
  ThumbsUp, Briefcase, Building2, MapPin, Clock, Phone
} from "lucide-react";

const iconMap = {
  Lightbulb, Zap, Users, Shield, Star, Heart, Award, TrendingUp,
  CheckCircle, Globe, Handshake, Target, Gem, Key, Leaf, Smile,
  ThumbsUp, Briefcase, Building2, MapPin, Clock, Phone
};

const useInView = (threshold = 0.1) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
};

const Values = ({ values }) => {
  const [titleRef, titleInView] = useInView(0.2);

  const title = values?.title || "لماذا الوسيم؟";
  const subtitle = values?.subtitle || "قيمنا التي تميزنا";
  const description = values?.description || "فريق نسائي متميز يعمل بشغف";

  const valuesList = values?.values || [
    { title: "الإبتكار", description: "نبتكر حلولاً عقارية متجددة تلبي احتياجاتكم", icon: "Lightbulb" },
    { title: "الكفاءة", description: "نقدم خدماتنا بأعلى معايير الكفاءة والاحترافية", icon: "Zap" },
    { title: "التعاون", description: "نعمل معكم كشركاء حقيقيين لتحقيق أهدافكم", icon: "Users" },
    { title: "الشفافية", description: "نلتزم بالشفافية التامة في جميع تعاملاتنا", icon: "Shield" },
  ];

  const timeline = values?.timeline || [
    { year: "2021", title: "تأسيس الشركة", description: "بدأت رحلة الوسيم في السوق السعودي" },
    { year: "2022", title: "افتتاح المقر", description: "افتتاح المقر الرئيسي في جازان" },
    { year: "2023", title: "التوسع والنمو", description: "تحقيق أكثر من 200 صفقة ناجحة" },
    { year: "2024", title: "المقر الجديد", description: "تعزيز الحضور في السوق العقاري" },
    { year: "2026", title: "التطوير العقاري", description: "إطلاق خدمات التطوير الشاملة" },
  ];

  return (
    <section id="values" data-testid="values-section" className="py-24 bg-white relative overflow-hidden">
      {/* Background texture */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/d60dcd94-e32b-483c-bd4a-5504cf1c9919/images/1e8381fde3dfa28b6090a7fd0c3131de20e23a69938c6643932310bda1878ae7.png)`,
        backgroundSize: "cover", backgroundPosition: "center"
      }}></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div ref={titleRef} className={`text-center mb-20 scroll-hidden ${titleInView ? "scroll-visible" : ""}`}>
          <p className="text-[#ecc775] font-bold text-sm uppercase tracking-widest mb-3">{subtitle}</p>
          <h2 className="text-4xl md:text-5xl font-black text-[#173269] mb-4">{title}</h2>
          <div className="section-divider mx-auto mb-6"></div>
          <p className="text-[#4A5568] text-lg max-w-2xl mx-auto">{description}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Values Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {valuesList.map((value, i) => {
              const Icon = iconMap[value.icon] || Shield;
              return (
                <ValueCard key={i} value={value} Icon={Icon} index={i} />
              );
            })}
          </div>

          {/* Timeline */}
          <Timeline timeline={timeline} />
        </div>
      </div>
    </section>
  );
};

const ValueCard = ({ value, Icon, index }) => {
  const [ref, inView] = useInView(0.1);
  return (
    <div
      ref={ref}
      data-testid={`value-card-${index}`}
      className={`p-6 border border-[#173269]/10 hover:border-[#ecc775] transition-all duration-300 hover:shadow-lg rounded-sm scroll-hidden ${inView ? "scroll-visible" : ""}`}
      style={{ transitionDelay: `${index * 0.12}s` }}
    >
      <div className="w-12 h-12 bg-[#173269]/5 rounded-sm flex items-center justify-center mb-4 border border-[#173269]/10">
        <Icon size={22} className="text-[#173269]" />
      </div>
      <div className="w-8 h-1 bg-[#ecc775] mb-3"></div>
      <h3 className="text-[#173269] font-bold text-lg mb-2">{value.title}</h3>
      <p className="text-[#4A5568] text-sm leading-relaxed">{value.description}</p>
    </div>
  );
};

const Timeline = ({ timeline }) => {
  const [ref, inView] = useInView(0.1);

  return (
    <div ref={ref} className={`relative scroll-hidden-right ${inView ? "scroll-visible-right" : ""}`}>
      <div className="mb-8">
        <h3 className="text-2xl font-black text-[#173269] mb-2">مسيرتنا عبر الزمن</h3>
        <div className="w-12 h-1 bg-[#ecc775]"></div>
      </div>

      {/* Timeline */}
      <div className="relative">
        {/* Vertical line */}
        <div className="absolute right-5 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#ecc775] to-[#173269]/20"></div>

        <div className="flex flex-col gap-0">
          {timeline.map((item, i) => (
            <div
              key={i}
              data-testid={`timeline-item-${i}`}
              className={`relative flex gap-6 pr-14 pb-8 scroll-hidden ${inView ? "scroll-visible" : ""}`}
              style={{ transitionDelay: `${i * 0.15}s` }}
            >
              {/* Circle */}
              <div className="absolute right-0 w-10 h-10 rounded-full bg-[#173269] border-4 border-[#ecc775] flex items-center justify-center flex-shrink-0 z-10">
                <span className="text-[#ecc775] text-xs font-black">{item.year.slice(-2)}</span>
              </div>

              {/* Content */}
              <div className="bg-[#F9F8F6] p-4 rounded-sm border border-[#173269]/8 flex-1 hover:border-[#ecc775]/40 transition-colors">
                <div className="flex items-center gap-3 mb-1">
                  <span className="text-[#ecc775] font-black text-sm">{item.year}</span>
                  <h4 className="text-[#173269] font-bold">{item.title}</h4>
                </div>
                <p className="text-[#4A5568] text-sm">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Values;
